import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

// Accessible toggle chip. Uses a native checkbox under the hood so keyboard
// navigation and screen readers work, replacing the custom-<button>-as-chip
// pattern that was scattered through BrowseProperties and ListPropertyNew
// (broken focus rings, no keyboard toggle, no a11y).

interface ChipProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  label: React.ReactNode;
  variant?: "solid" | "outline";
  size?: "default" | "lg";
}

const Chip = React.forwardRef<HTMLInputElement, ChipProps>(
  ({ label, variant = "outline", size = "default", className, checked, ...props }, ref) => {
    const sizeClasses =
      size === "lg" ? "px-4 py-3 text-sm" : "px-3 py-1.5 text-[13px]";
    const baseLabel = `group inline-flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-chip border transition-colors select-none ${sizeClasses}`;
    const stateClasses =
      variant === "solid"
        ? checked
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-foreground border-border hover:bg-muted/60"
        : checked
          ? "bg-primary/10 text-primary border-primary"
          : "bg-card text-foreground border-border hover:border-foreground/30";
    return (
      <label className={cn(baseLabel, stateClasses, className)}>
        <input
          ref={ref}
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          {...props}
        />
        <span className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring rounded-full -m-px p-px inline-flex items-center gap-1.5 min-w-0">
          {checked && <Check className="h-3 w-3 flex-shrink-0" aria-hidden />}
          <span className="truncate">{label}</span>
        </span>
      </label>
    );
  },
);
Chip.displayName = "Chip";

export { Chip };
